
// ============================================
// Cart Utility Functions
// ============================================

import { CartProduct, Product } from "./Dto/AddToCardtUtilityDto";

/**
 * Transforms a product into a cart-ready format
 * @param product - The product to transform
 * @param imageUrl - Optional custom image URL to use
 * @returns Transformed product ready for cart
 */
export function transformProductForCart(product: Product, imageUrl?: string): Product {
  const defaultVariant = product.variants?.find((v: { isDefault: any; }) => v.isDefault) || product.variants?.[0];
  
  return {
    id: product._id || product.id,
    _id: product._id || product.id,
    name: product.title || product.name || 'Unnamed Product',
    title: product.title || product.name || 'Unnamed Product',
    price: defaultVariant?.price || product.price || 0,
    image: imageUrl || product.image || product.imageUrl || '',
    imageUrl: imageUrl || product.image || product.imageUrl || '',
    slug: product.slug || product.id,
    description: product.description || '',
    categories: product.categories?.map((cat: any) => 
      typeof cat === 'string' ? cat : cat.name || cat
    ) || [],
    variants: product.variants || [],
    sku: product.sku || defaultVariant?.sku || '',
    quantity: product.quantity || 0,
    compareAtPrice: product.compareAtPrice || undefined,
    reviewCount: product.ratingsQuantity || product.reviewCount || 0,
    ratingsQuantity: product.ratingsQuantity || 0,
    rating: product.rating || 0,
    weight: product.weight || 0,
    weightUnit: product.weightUnit || 'g',
    isActive: product.isActive ?? true,
    isFeatured: product.isFeatured ?? false,
    hasVariants: product.hasVariants ?? false,
    requiresShipping: product.requiresShipping ?? true,
    taxable: product.taxable ?? true,
    tags: product.tags || [],
    trackInventory: product.trackInventory ?? false,
    inventoryPolicy: product.inventoryPolicy || 'deny',
    meta: product.meta || { views: 0, purchases: 0 },
    createdAt: product.createdAt || new Date().toISOString(),
    updatedAt: product.updatedAt || new Date().toISOString(),
    __v: product.__v || 0
  };
}

/**
 * Gets the default variant from a product
 * @param product - The product to get the variant from
 * @returns The default variant or first variant or undefined
 */
export function getDefaultVariant(product: Product) {
  return product.variants?.find((v: { isDefault: any; }) => v.isDefault) || product.variants?.[0];
}

/**
 * Gets the variant SKU from a product
 * @param product - The product to get the SKU from
 * @returns The variant SKU or empty string
 */
export function getVariantSku(product: Product): string {
  const defaultVariant = getDefaultVariant(product);
  return defaultVariant?.sku || '';
}

/**
 * Checks if a product can be added to cart
 * @param product - The product to check
 * @returns True if product can be added to cart
 */
export function canAddToCart(product: Product): boolean {
  return (
    product.quantity !== undefined &&
    product.quantity > 0 &&
    product.isActive !== false
  );
}

/**
 * Gets the display price for a product (considering variants)
 * @param product - The product to get price from
 * @returns The price to display
 */
export function getDisplayPrice(product: Product): number {
  const defaultVariant = getDefaultVariant(product);
  return defaultVariant?.price || product.price || 0;
}

/**
 * Gets the image URL for a product (considering variants)
 * @param product - The product to get image from
 * @param fallbackUrl - Optional fallback URL
 * @returns The image URL to display
 */
export function getProductImageUrl(product: Product, fallbackUrl?: string): string {
  const defaultVariant = getDefaultVariant(product);
  return (
    defaultVariant?.imageUrl ||
    product.image ||
    product.imageUrl ||
    fallbackUrl ||
    ''
  );
}

/**
 * Checks if a product has multiple variants
 * @param product - The product to check
 * @returns True if product has multiple variants
 */
export function hasMultipleVariants(product: Product): boolean {
  return (product.variants?.length || 0) > 1;
}

/**
 * Gets variant information for display
 * @param product - The product to get variant info from
 * @param variantSku - Optional specific variant SKU
 * @returns Variant information or null
 */
export function getVariantInfo(product: Product, variantSku?: string) {
  if (!variantSku) {
    return getDefaultVariant(product);
  }
  return product.variants?.find((v: { sku: string; }) => v.sku === variantSku) || null;
}

/**
 * Formats product categories for display
 * @param categories - The categories to format
 * @returns Formatted category array
 */
export function formatCategories(categories?: any[]): string[] {
  if (!categories || !Array.isArray(categories)) return [];
  
  return categories.map((cat: any) => {
    if (typeof cat === 'string') return cat;
    return cat.name || cat.title || String(cat);
  }).filter(Boolean);
}

/**
 * Checks if a product is in stock
 * @param product - The product to check
 * @param requestedQuantity - The quantity to check (default: 1)
 * @returns True if product is in stock
 */
export function isInStock(product: Product, requestedQuantity: number = 1): boolean {
  if (!product.trackInventory) return true;
  return (product.quantity || 0) >= requestedQuantity;
}

/**
 * Gets the maximum quantity that can be added to cart
 * @param product - The product to check
 * @param maxLimit - Maximum limit (default: 99)
 * @returns Maximum quantity available
 */
export function getMaxQuantity(product: Product, maxLimit: number = 99): number {
  if (!product.trackInventory) return maxLimit;
  return Math.min(product.quantity || 0, maxLimit);
}

/**
 * Calculates discount percentage
 * @param price - Current price
 * @param compareAtPrice - Original price
 * @returns Discount percentage or 0
 */
export function calculateDiscountPercentage(price: number, compareAtPrice?: number | null): number {
  if (!compareAtPrice || compareAtPrice <= price) return 0;
  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

/**
 * Checks if a product has a discount
 * @param product - The product to check
 * @returns True if product has a discount
 */
export function hasDiscount(product: Product): boolean {
  const price = getDisplayPrice(product);
  return !!(product.compareAtPrice && product.compareAtPrice > price);
}

/**
 * Creates a cart product payload
 * @param product - The product to create payload from
 * @param quantity - Quantity to add
 * @param variantSku - Optional variant SKU
 * @returns Cart product payload
 */
export function createCartProductPayload(
  product: Product,
  quantity: number = 1,
  variantSku?: string
): CartProduct {
  const variant = variantSku 
    ? product.variants?.find((v: { sku: string; }) => v.sku === variantSku)
    : getDefaultVariant(product);

  return {
    productId: product._id || product.id,
    variantSku: variantSku || variant?.sku || '',
    variantId: variantSku || variant?.sku || '',
    variantTitle: variant?.name || product.title || product.name || '',
    variantImage: variant?.imageUrl || product.image || '',
    quantity,
    variantInfo: variant ? {
      name: variant.name || product.title || product.name || 'Default Variant',
      sku: variant.sku,
      price: variant.price || product.price || 0,
      quantity,
      weight: variant.weight || product.weight || 0,
      weightUnit: variant.weightUnit || product.weightUnit || 'g',
      imageUrl: variant.imageUrl || product.image || '',
      requiresShipping: variant.requiresShipping ?? product.requiresShipping ?? true,
      taxable: variant.taxable ?? product.taxable ?? true,
      trackInventory: variant.trackInventory ?? product.trackInventory ?? false,
      isDefault: variant.isDefault || false
    } : undefined,
    description: product.description || '',
    imageUrl: product.image || product.imageUrl || '',
    name: product.title || product.name || '',
    price: variant?.price || product.price || 0
  };
}

// ============================================
// Cart Action Handlers
// ============================================

/**
 * Handler for adding product to cart
 * @param product - The product to add
 * @param addToCart - The cart context addItem function
 * @param options - Additional options
 * @returns Promise that resolves when item is added
 */
export async function handleAddToCart(
  product: Product,
  addToCart: (product: Product, quantity?: number, variantSku?: string) => Promise<void>,
  options?: {
    quantity?: number;
    variantSku?: string;
    imageUrl?: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }
): Promise<void> {
  const {
    quantity = 1,
    variantSku,
    imageUrl,
    onSuccess,
    onError
  } = options || {};

  // Check if product can be added to cart
  if (!canAddToCart(product)) {
    onError?.(new Error('Product is out of stock or unavailable'));
    return;
  }

  // Get the variant SKU if not provided
  const finalVariantSku = variantSku || getVariantSku(product);

  // Transform product for cart
  const productForCart = transformProductForCart(product, imageUrl);

  try {
    await addToCart(productForCart, quantity, finalVariantSku);
    onSuccess?.();
  } catch (error) {
    onError?.(error);
    throw error;
  }
}

/**
 * React event handler wrapper for adding to cart
 * @param e - React mouse event
 * @param product - The product to add
 * @param addToCart - The cart context addItem function
 * @param options - Additional options
 */
export async function handleAddToCartClick(
  e: React.MouseEvent,
  product: Product,
  addToCart: (product: Product, quantity?: number, variantSku?: string) => Promise<void>,
  options?: {
    cartLoading?: boolean;
    quantity?: number;
    variantSku?: string;
    imageUrl?: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
    preventNavigation?: boolean;
  }
): Promise<void> {
  const {
    cartLoading = false,
    preventNavigation = true,
    ...restOptions
  } = options || {};

  // Prevent default behavior
  if (preventNavigation) {
    e.preventDefault();
    e.stopPropagation();
  }

  // Don't proceed if cart is loading or product is out of stock
  if (cartLoading || !canAddToCart(product)) {
    return;
  }

  await handleAddToCart(product, addToCart, restOptions);
}

/**
 * Batch add multiple products to cart
 * @param products - Array of products with quantities
 * @param addToCart - The cart context addItem function
 * @returns Promise that resolves when all items are added
 */
export async function batchAddToCart(
  products: Array<{ product: Product; quantity?: number; variantSku?: string }>,
  addToCart: (product: Product, quantity?: number, variantSku?: string) => Promise<void>
): Promise<{ success: number; failed: number; errors: any[] }> {
  const results = {
    success: 0,
    failed: 0,
    errors: [] as any[]
  };

  for (const item of products) {
    try {
      await handleAddToCart(item.product, addToCart, {
        quantity: item.quantity,
        variantSku: item.variantSku
      });
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push({
        product: item.product,
        error
      });
    }
  }

  return results;
}